import type { Corner } from "../types"

// Where the person is walking, from the phone's own motion sensors (gyroscope
// based: no compass, a car's steel body throws a compass off). Only TURNS
// between photos are used, never absolute headings. Pure; unit-tested.
//
// Around the car, seen from above with the front at the top, each corner's
// stand point has a bearing from the car's centre, clockwise from the front.
// Standing there and facing the car, the phone points the opposite way, so
// walking clockwise round the car turns the phone clockwise by the same angle.

export const STAND_BEARING: Record<Corner, number> = {
  FL: -37.6,
  FR: 37.6,
  RR: 142.4,
  RL: -142.4,
}

// Wrap to (-180, 180].
export function angleDiff(a: number, b: number): number {
  let d = (a - b) % 360
  if (d > 180) d -= 360
  if (d <= -180) d += 360
  return d
}

// Which way the back camera looks, in degrees clockwise (like a compass, but
// relative), from a DeviceOrientationEvent's alpha/beta/gamma (W3C
// Z-X'-Y'' angles; the back camera looks along the device's -z). Null when
// the phone is lying flat and has no meaningful direction.
export function cameraYaw(alpha: number | null, beta: number | null, gamma: number | null): number | null {
  if (alpha === null || beta === null || gamma === null) return null
  const r = Math.PI / 180
  const [ca, sa, cb, sb, cg, sg] = [
    Math.cos(alpha * r),
    Math.sin(alpha * r),
    Math.cos(beta * r),
    Math.sin(beta * r),
    Math.cos(gamma * r),
    Math.sin(gamma * r),
  ]
  // -(R · ẑ) with R = Rz(alpha) · Rx(beta) · Ry(gamma), world = east/north/up.
  const east = -ca * sg - sa * sb * cg
  const north = -sa * sg + ca * sb * cg
  if (Math.hypot(east, north) < 0.35) return null
  return (Math.atan2(east, north) * 180) / Math.PI
}

export type TurnJudgement =
  // Turned about as much as walking to the asked corner takes.
  | "expected"
  // Barely turned: the photo is from the same spot as the last one.
  | "same-spot"
  // Turned, but to somewhere else (the other way round, or across the car).
  | "elsewhere"

export const TURN = {
  SAME_SPOT: 22,
  TOLERANCE: 40,
} as const

export function judgeTurn(from: Corner, to: Corner, turned: number): TurnJudgement {
  if (Math.abs(turned) < TURN.SAME_SPOT && from !== to) return "same-spot"
  const expected = angleDiff(STAND_BEARING[to], STAND_BEARING[from])
  return Math.abs(angleDiff(turned, expected)) <= TURN.TOLERANCE ? "expected" : "elsewhere"
}

// Where on the walk the person is now: the bearing of the corner where the
// first whole-car photo was taken, plus everything turned since.
export function bearingNow(firstCorner: Corner, yawAtFirst: number, yawNow: number): number {
  return angleDiff(STAND_BEARING[firstCorner] + angleDiff(yawNow, yawAtFirst), 0)
}

export function nearestCorner(bearing: number): Corner {
  let best: Corner = "FL"
  let bestD = Infinity
  for (const c of Object.keys(STAND_BEARING) as Corner[]) {
    const d = Math.abs(angleDiff(bearing, STAND_BEARING[c]))
    if (d < bestD) {
      bestD = d
      best = c
    }
  }
  return best
}

// Close enough to a corner's stand point to call it "there".
export function atCorner(bearing: number, corner: Corner): boolean {
  return Math.abs(angleDiff(bearing, STAND_BEARING[corner])) <= 25
}
